import * as React from 'react';
import { observer } from 'mobx-react';
import { Routes } from '../../routes';
import { PageHeadingWidget } from '../../widgets/page/PageHeadingWidget';
import { ProfilePageWidget } from '../../widgets/page/ProfilePageWidget';
import { styled } from '../../theme';
import { FormLayoutWidget } from '../../widgets/layout/FormLayoutWidget';
import { GroupedButtons } from '../../widgets/buttons/GroupedButtons';
import { FormikInputWidget } from '../../widgets/inputs/FormikInput';
import { FormikContext, useFormik } from 'formik';
import { useSystem } from '../../hooks/useSystem';
import { useState } from 'react';

export const ProfileGeneralPage: React.FC = observer(() => {
  const system = useSystem();
  const [loading, setLoading] = useState(false);
  const formik = useFormik<{ name: string; email: string }>({
    initialValues: {
      name: system.context.USER_NAME || '',
      email: system.context.USER_EMAIL || ''
    },
    onSubmit: async ({ name }) => {
      try {
        setLoading(true);
        await system.sessionAccountsPortalClient.updateDetails({
          name: name
        });
        system.context.USER_NAME = name;
        system.dialogStore.infoDialog({
          title: 'Details updated',
          message: 'You may need to sign out and in again before your changes take full effect across all applications.'
        });
      } catch (ex) {
        system.dialogStore.errorDialog({
          title: 'Oops, something went wrong',
          errorMessage: 'Failed to update user details'
        });
      } finally {
        setLoading(false);
      }
    },
    validateOnBlur: false,
    validate: (values) => {
      if (values.name.trim() === '') {
        return {
          name: 'Name must be set'
        };
      }
    }
  });

  return (
    <ProfilePageWidget loading={loading} activeItemKey={Routes.PROFILE_GENERAL}>
      <S.Container>
        <PageHeadingWidget title={'User Details'} />
        <FormikContext.Provider value={formik}>
          <S.FormContainer>
            <FormLayoutWidget
              sections={[
                {
                  key: 'details',
                  elements: [
                    <>
                      <S.Input name="name" label="Name" />
                      <S.Input name="email" label="Email" disabled={true} />
                    </>
                  ]
                },
                {
                  key: 'submit',
                  elements: [
                    <GroupedButtons
                      btns={[
                        {
                          label: 'Update details',
                          disabled: false,
                          action: async (event, loading) => {
                            try {
                              loading(true);
                              await formik.submitForm();
                            } finally {
                              loading(false);
                            }
                          }
                        }
                      ]}
                    />
                  ]
                }
              ]}
            />
          </S.FormContainer>
        </FormikContext.Provider>
      </S.Container>
    </ProfilePageWidget>
  );
});

namespace S {
  export const Container = styled.div`
    margin: auto;
    margin-bottom: 20px;
    width: 600px;
  `;

  export const FormContainer = styled.div`
    background: ${(p) => p.theme.forms.panelBackground};
    padding: 30px;
    border-radius: 10px;
    box-shadow: 0 0 20px rgba(0, 0, 0, 0.2);
  `;

  export const Input = styled(FormikInputWidget)`
    margin-bottom: 15px;

    &:last-of-type {
      margin-bottom: 0;
    }
  `;
}
